import {
  type LocationGroup,
  useGetSchemeGroupsSchemeSchemeIdGroupsGet,
} from "kubb";
import { isNil } from "lodash-es";
import React from "react";
import { useRequiredSelectedCompanyId } from "../../../reducers/company.tsx";
import { SchemeContext } from "../../../reducers/scheme";
import {
  SelectComboField,
  type SelectComboFieldProps,
} from "../../Forms/SelectCombo/SelectCombo";
import { locationGroupListToOptions } from "../model";

type LocationGroupSelectComboProps = Omit<
  SelectComboFieldProps,
  "options" | "error"
> &
  (
    | {
        /* optionally set the scheme for which groups should be displayed/loaded, otherwise uses selected schehem */
        useGivenSchemeId?: true;
        schemeId?: string | null;
      }
    | {
        useGivenSchemeId?: false;
      }
  );

function InnerGroupSelect(
  props: {
    loading: boolean;
    error?: Error | null;
    groups?: LocationGroup[];
  } & Omit<SelectComboFieldProps, "options" | "error">,
) {
  const { disabled, loading, groups, error, ...rest } = props;
  const ready = !loading && !error;
  const haveGroups = groups && groups.length > 0;
  const altText: string = ready
    ? haveGroups
      ? "All"
      : "No Groups"
    : loading
      ? "Loading Groups"
      : error?.message || "Error Loading Groups";

  return (
    <SelectComboField
      {...rest}
      options={
        haveGroups
          ? [
              { label: "All", value: "undefined" },
              { label: "None", value: "NONE" },
              ...locationGroupListToOptions(groups as LocationGroup[], 0),
            ]
          : []
      }
      placeholder={altText}
      disabled={!haveGroups || disabled}
    />
  );
}

function SelectedSchemeSelectCombo(
  props: Omit<SelectComboFieldProps, "options" | "error">,
) {
  const locationGroups = React.useContext(SchemeContext).state.groups;
  return (
    <InnerGroupSelect
      loading={locationGroups.loading}
      error={locationGroups.error}
      groups={locationGroups.groups}
      {...props}
    />
  );
}

function GivenSchemeSelectCombo(
  props: LocationGroupSelectComboProps & { schemeId: string },
) {
  const companyId = useRequiredSelectedCompanyId();
  const { schemeId, ...rest } = props;
  const { data, isLoading, error } = useGetSchemeGroupsSchemeSchemeIdGroupsGet(
    schemeId,
    {
      company_id: companyId,
    },
  );

  return (
    <InnerGroupSelect
      {...rest}
      groups={data?.data}
      error={error}
      loading={isLoading}
    />
  );
}

function LocationGroupSelectCombo(props: LocationGroupSelectComboProps) {
  const { useGivenSchemeId, ...rest } = props;
  if (useGivenSchemeId) {
    const { schemeId } = rest as Omit<SelectComboFieldProps, "options"> & {
      useGivenSchemeId?: true;
      schemeId?: string | null;
    };
    if (!isNil(schemeId)) {
      return <GivenSchemeSelectCombo {...rest} schemeId={schemeId} />;
    } else {
      return <>Placeholder</>;
    }
  } else {
    return <SelectedSchemeSelectCombo {...rest} />;
  }
}

export default LocationGroupSelectCombo;
